import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import {
  fetchPayoutTiers,
  createPayoutTier,
  updatePayoutTier,
} from "../../actions/referralActions";
import useHasPermission from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissions";
import {
  modalOverlay,
  modalBox,
  modalHeader,
  modalBody,
  closeBtn,
} from "./shared";

const emptyForm = {
  rank_from: "",
  rank_to: "",
  payout_amount: "",
  payout_currency: "NGN",
  is_active: true,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #444",
  borderRadius: "6px",
  background: "#1a1a1a",
  color: "#fff",
  fontSize: "14px",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  color: "#aaa",
  marginBottom: "4px",
  fontWeight: "500",
};

const PayoutTiers = () => {
  const dispatch = useDispatch();
  const canManage = useHasPermission(PERMISSIONS.MANAGE_LEADERBOARD_TIERS);

  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const { loading, data, error } = useSelector((state) => state.getPayoutTiers) || {};
  const { loading: createLoading } = useSelector((state) => state.createPayoutTier) || {};
  const { loading: updateLoading } = useSelector((state) => state.updatePayoutTier) || {};

  const tiers = data?.data;

  useEffect(() => {
    dispatch(fetchPayoutTiers(showActiveOnly));
  }, [showActiveOnly]);

  const openCreate = () => {
    setEditingTier(null);
    setForm(emptyForm);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const openEdit = (tier) => {
    setEditingTier(tier);
    setForm({
      rank_from: tier.rank_from ?? "",
      rank_to: tier.rank_to ?? "",
      payout_amount: tier.payout_amount ?? "",
      payout_currency: tier.payout_currency || "NGN",
      is_active: tier.is_active ?? true,
    });
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTier(null);
    setForm(emptyForm);
    document.body.style.overflow = "auto";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rank_from) { toast.error("Rank from is required"); return; }
    if (!form.rank_to) { toast.error("Rank to is required"); return; }
    if (!form.payout_amount) { toast.error("Payout amount is required"); return; }

    setSubmitting(true);
    const body = {
      rank_from: Number(form.rank_from),
      rank_to: Number(form.rank_to),
      payout_amount: Number(form.payout_amount),
      payout_currency: form.payout_currency.trim(),
      is_active: form.is_active,
    };

    try {
      if (editingTier) {
        await dispatch(updatePayoutTier(editingTier.id, body));
        toast.success("Payout tier updated");
      } else {
        await dispatch(createPayoutTier(body));
        toast.success("Payout tier created");
      }
      closeModal();
      dispatch(fetchPayoutTiers(showActiveOnly));
    } catch (err) {
      toast.error(err?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-danger text-center">{error}</p>;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
          <input type="checkbox" checked={showActiveOnly} onChange={(e) => setShowActiveOnly(e.target.checked)} />
          Active only
        </label>
        {canManage && (
          <button onClick={openCreate} style={{ padding: "8px 18px", background: "#007bff", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "14px" }}>
            + Add Tier
          </button>
        )}
      </div>

      <MDBTable responsive style={{ width: "100%", fontSize: "14px" }}>
        <MDBTableHead>
          <tr>
            <th>Rank From</th>
            <th>Rank To</th>
            <th>Payout Amount</th>
            <th>Currency</th>
            <th>Status</th>
            {canManage && <th>Action</th>}
          </tr>
        </MDBTableHead>
        <MDBTableBody>
          {tiers?.length > 0 ? (
            tiers.map((item) => (
              <tr key={item.id}>
                <td>#{item.rank_from}</td>
                <td>#{item.rank_to}</td>
                <td style={{ fontWeight: "600" }}>{item.payout_amount?.toLocaleString()}</td>
                <td>{item.payout_currency}</td>
                <td>
                  <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", background: item.is_active ? "#28a74533" : "#6c757d33", color: item.is_active ? "#28a745" : "#6c757d" }}>
                    {item.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                {canManage && (
                  <td>
                    <p className="withdrawal-btn" onClick={() => openEdit(item)} style={{ cursor: "pointer", color: "white", width: "70px", textAlign: "center", margin: 0 }}>
                      Edit
                    </p>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={canManage ? "6" : "5"} style={{ textAlign: "center", padding: "20px" }}>No payout tiers found</td>
            </tr>
          )}
        </MDBTableBody>
      </MDBTable>

      {showModal && (
        <div style={modalOverlay} onClick={closeModal}>
          <div style={{ ...modalBox, maxWidth: "460px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <h3 style={{ margin: 0, fontSize: "18px" }}>{editingTier ? "Edit Payout Tier" : "Create Payout Tier"}</h3>
              <button style={closeBtn} onClick={closeModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              <form onSubmit={handleSubmit}>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={labelStyle}>Rank From *</label>
                      <input type="number" name="rank_from" value={form.rank_from} onChange={handleChange} style={inputStyle} min="1" placeholder="e.g. 1" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Rank To *</label>
                      <input type="number" name="rank_to" value={form.rank_to} onChange={handleChange} style={inputStyle} min="1" placeholder="e.g. 3" required />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={labelStyle}>Payout Amount *</label>
                      <input type="number" name="payout_amount" value={form.payout_amount} onChange={handleChange} style={inputStyle} min="0" step="0.01" placeholder="e.g. 50000" required />
                    </div>
                    <div>
                      <label style={labelStyle}>Currency</label>
                      <input name="payout_currency" value={form.payout_currency} onChange={handleChange} style={inputStyle} placeholder="NGN" maxLength={10} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", cursor: "pointer" }}>
                      <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                      Active
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", paddingTop: "8px" }}>
                    <button type="button" onClick={closeModal} style={{ padding: "10px 20px", border: "1px solid #444", borderRadius: "6px", background: "transparent", color: "#fff", cursor: "pointer" }}>
                      Cancel
                    </button>
                    <button type="submit" disabled={submitting || createLoading || updateLoading} style={{ padding: "10px 24px", background: "#007bff", color: "#fff", border: "none", borderRadius: "6px", cursor: submitting ? "not-allowed" : "pointer", fontWeight: "600", opacity: submitting ? 0.7 : 1 }}>
                      {submitting ? "Saving..." : editingTier ? "Update" : "Create"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PayoutTiers;
